import React, { useState } from 'react';
import { Button, makeStyles, Theme, Typography, MenuItem, TextField } from '@material-ui/core';
import NETWORK_CONSTANTS from '../../../constants/network.constants';
import { lightGrey } from '../../../app/theme';
import STORAGE_CONSTANTS from '../../../constants/storage.constants';

const useStyles = makeStyles((theme: Theme) => ({
  screenText: {
    maxWidth: 900,
    padding: '35px 0px 65px',
  },
  header: {
    fontSize: 20,
    fontWeight: 600,
  },
  paragraph: {
    fontSize: 14,
    paddingBottom: 16,
    lineHeight: 1.15,
  },
  exportTitle: {
    margin: '17px 0 30px 0',
    // margin: '17px 0 30px 120px', with invite user form
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    minWidth: 465,
    color: theme.palette.secondary.main,
    fontSize: 32,
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
    },
  },
  inputField: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  downloadButtonContainer: {
    textAlign: 'left',
    // textAlign: 'right', with invite user form
  },
  exportControls: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))',
    gap: 20,
    marginBottom: 20,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  dateControls: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(140px, 1fr))',
    gap: 20,
    marginBottom: 20,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  monthControl: {
    width: 'calc((100% - 40px) / 3)',
    marginBottom: 20,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  selectionSummary: {
    fontSize: 14,
    marginBottom: 20,
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: 14,
    marginBottom: 16,
  },
  downloadButton: {
    marginLeft: 0,
    marginRight: 40,
    // marginLeft: 40, with invite user form
    // marginRight: 0, with invite user form
    marginBottom: 20,
    borderRadius: 21,
    height: 42,
    minWidth: 104,
  },
}));

type AnalyticsProduct = 'axumin' | 'posluma';
type AnalyticsPeriod = 'month' | 'quarter' | 'all' | 'custom';
type AnalyticsFormat = 'csv' | 'zip';
const ORGANIZATION_IDS: Record<AnalyticsProduct, number> = {
  axumin: 1,
  posluma: 2,
};

const PRODUCT_LABELS: Record<AnalyticsProduct, string> = {
  axumin: 'Axumin',
  posluma: 'POSLUMA',
};

const PERIOD_LABELS: Record<AnalyticsPeriod, string> = {
  month: 'By month',
  quarter: 'By quarter',
  all: 'Full history (all available data)',
  custom: 'Custom date range',
};

const getEasternDateParts = (): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const partValue = (type: string): number => Number(parts.find((part) => part.type === type)?.value);

  return {
    year: partValue('year'),
    month: partValue('month'),
    day: partValue('day'),
  };
};

const formatDateParts = (year: number, month: number, day: number): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const getAnalyticsFilename = (
  product: AnalyticsProduct,
  period: AnalyticsPeriod,
  format: AnalyticsFormat,
  startDate: string,
  endDate: string,
  selectedMonth: string,
  selectedQuarterYear: string,
  selectedQuarter: string,
): string => {
  const { year, month, day } = getEasternDateParts();
  let periodName = '';

  if (period === 'all') {
    periodName = `All_${formatDateParts(year, month, day)}`;
  } else if (period === 'month') {
    periodName = selectedMonth;
  } else if (period === 'quarter') {
    periodName = `${selectedQuarterYear}-Q${selectedQuarter}`;
  } else {
    periodName = `${startDate}_to_${endDate}`;
  }

  return `Blue_Earth_${PRODUCT_LABELS[product]}_Analytics_${periodName}.${format}`;
};

const ExportAnalytics = (): JSX.Element => {
  const classes = useStyles();
  const easternDate = getEasternDateParts();
  const currentMonth = `${easternDate.year}-${String(easternDate.month).padStart(2, '0')}`;
  const currentQuarter = Math.ceil(easternDate.month / 3);
  const [product, setProduct] = useState<AnalyticsProduct>('axumin');
  const [period, setPeriod] = useState<AnalyticsPeriod>('all');
  const [fileFormat, setFileFormat] = useState<AnalyticsFormat>('csv');
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedQuarterYear, setSelectedQuarterYear] = useState(String(easternDate.year));
  const [selectedQuarter, setSelectedQuarter] = useState(String(currentQuarter));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const today = formatDateParts(easternDate.year, easternDate.month, easternDate.day);
  let selectedPeriodLabel = PERIOD_LABELS[period];
  if (period === 'month') {
    selectedPeriodLabel = selectedMonth
      ? new Intl.DateTimeFormat('en-US', {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(`${selectedMonth}-01T00:00:00Z`))
      : 'Select a month';
  } else if (period === 'quarter') {
    selectedPeriodLabel = `${selectedQuarterYear} Q${selectedQuarter}`;
  }

  const downloadAnalytics = async () => {
    setDownloadError('');

    if (period === 'custom' && (!startDate || !endDate)) {
      setDownloadError('Select both a start date and an end date.');
      return;
    }

    if (period === 'custom' && startDate > endDate) {
      setDownloadError('The start date must be before or equal to the end date.');
      return;
    }

    if (period === 'custom' && endDate > today) {
      setDownloadError('The end date cannot be later than today.');
      return;
    }

    if (period === 'month' && (!selectedMonth || selectedMonth > currentMonth)) {
      setDownloadError('Select a valid month that is not in the future.');
      return;
    }

    const quarterYear = Number(selectedQuarterYear);
    const quarterNumber = Number(selectedQuarter);
    if (
      period === 'quarter' &&
      (!quarterYear ||
        quarterNumber < 1 ||
        quarterNumber > 4 ||
        quarterYear > easternDate.year ||
        (quarterYear === easternDate.year && quarterNumber > currentQuarter))
    ) {
      setDownloadError('Select a valid quarter that is not in the future.');
      return;
    }

    setIsDownloading(true);

    try {
      const response = await fetch(`${NETWORK_CONSTANTS.BASE_URL}${NETWORK_CONSTANTS.API_DOWNLOAD_ZIP}`, {
        method: 'POST',
        body: JSON.stringify({
          org_id: ORGANIZATION_IDS[product],
          period,
          format: fileFormat,
          ...(period === 'month' ? { month: selectedMonth } : {}),
          ...(period === 'quarter' ? { year: quarterYear, quarter: quarterNumber } : {}),
          ...(period === 'custom' ? { start_date: startDate, end_date: endDate } : {}),
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${localStorage.getItem(STORAGE_CONSTANTS.TOKEN_KEY)}`,
        },
      });

      if (!response.ok) {
        const responseData: { error?: string } = await response.json().catch(() => ({}));
        throw new Error(responseData.error || 'The report could not be generated. Please try again.');
      }

      const responseType = (response.headers.get('Content-Type') || '').toLowerCase();
      if (fileFormat === 'csv' && responseType.includes('zip')) {
        throw new Error('The server returned a ZIP instead of the requested CSV. Please try again.');
      }

      const blob = await response.blob();
      const signature = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
      const isZipFile = signature[0] === 0x50 && signature[1] === 0x4b;
      if (fileFormat === 'csv' && isZipFile) {
        throw new Error(
          'The server returned a ZIP instead of the requested CSV. Please try again after the backend update is deployed.',
        );
      }
      if (fileFormat === 'zip' && !isZipFile) {
        throw new Error('The server did not return a valid ZIP file. Please try CSV or contact support.');
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
      const responseFilename = filenameMatch ? filenameMatch[1] : '';
      const filenameMatchesFormat = responseFilename.toLowerCase().endsWith(`.${fileFormat}`);
      link.href = objectUrl;
      link.download = filenameMatchesFormat
        ? responseFilename
        : getAnalyticsFilename(
            product,
            period,
            fileFormat,
            startDate,
            endDate,
            selectedMonth,
            selectedQuarterYear,
            selectedQuarter,
          );
      link.setAttribute('style', 'visibility:hidden');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'The download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section aria-labelledby="export-analytics-title">
      <Typography id="export-analytics-title" className={classes.exportTitle}>
        Export Analytics
      </Typography>
      <div className={classes.exportControls}>
        <TextField
          select
          fullWidth
          variant="outlined"
          label="PRODUCT"
          value={product}
          onChange={(event) => setProduct(event.target.value as AnalyticsProduct)}
          InputLabelProps={{ className: classes.inputField }}
        >
          <MenuItem value="axumin">Axumin</MenuItem>
          <MenuItem value="posluma">POSLUMA</MenuItem>
        </TextField>
        <TextField
          select
          fullWidth
          variant="outlined"
          label="REPORTING PERIOD"
          value={period}
          onChange={(event) => setPeriod(event.target.value as AnalyticsPeriod)}
          InputLabelProps={{ className: classes.inputField }}
        >
          <MenuItem value="month">By month</MenuItem>
          <MenuItem value="quarter">By quarter</MenuItem>
          <MenuItem value="all">Full history (all available data)</MenuItem>
          <MenuItem value="custom">Custom date range</MenuItem>
        </TextField>
        <TextField
          select
          fullWidth
          variant="outlined"
          label="FILE FORMAT"
          value={fileFormat}
          onChange={(event) => setFileFormat(event.target.value as AnalyticsFormat)}
          InputLabelProps={{ className: classes.inputField }}
        >
          <MenuItem value="csv">CSV</MenuItem>
          <MenuItem value="zip">ZIP</MenuItem>
        </TextField>
      </div>
      {period === 'month' && (
        <div className={classes.monthControl}>
          <TextField
            fullWidth
            variant="outlined"
            label="MONTH"
            type="month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            InputLabelProps={{ shrink: true, className: classes.inputField }}
            inputProps={{ max: currentMonth }}
          />
        </div>
      )}
      {period === 'quarter' && (
        <div className={classes.dateControls}>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="QUARTER"
            value={selectedQuarter}
            onChange={(event) => setSelectedQuarter(event.target.value)}
            InputLabelProps={{ className: classes.inputField }}
          >
            {[1, 2, 3, 4].map((quarter) => (
              <MenuItem
                key={quarter}
                value={String(quarter)}
                disabled={Number(selectedQuarterYear) === easternDate.year && quarter > currentQuarter}
              >
                {`Q${quarter}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            variant="outlined"
            label="YEAR"
            type="number"
            value={selectedQuarterYear}
            onChange={(event) => setSelectedQuarterYear(event.target.value)}
            InputLabelProps={{ shrink: true, className: classes.inputField }}
            inputProps={{ min: 2000, max: easternDate.year }}
          />
        </div>
      )}
      {period === 'custom' && (
        <div className={classes.dateControls}>
          <TextField
            fullWidth
            variant="outlined"
            label="START DATE"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true, className: classes.inputField }}
            inputProps={{ max: endDate || today }}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="END DATE"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true, className: classes.inputField }}
            inputProps={{ min: startDate || undefined, max: today }}
          />
        </div>
      )}
      <Typography className={classes.selectionSummary}>
        {`Selected report: ${PRODUCT_LABELS[product]} • ${selectedPeriodLabel} • ${fileFormat.toUpperCase()}`}
      </Typography>
      {downloadError && <Typography className={classes.errorMessage}>{downloadError}</Typography>}
      <div className={classes.downloadButtonContainer}>
        <Button
          className={classes.downloadButton}
          variant="contained"
          color="secondary"
          disabled={isDownloading}
          onClick={downloadAnalytics}
        >
          {isDownloading ? 'Preparing report…' : 'Download report'}
        </Button>
      </div>
      <div className={classes.screenText}>
        <Typography className={classes.header}>To Export Analytics:</Typography>
        <Typography className={classes.paragraph}>
          Select a product, reporting period, and file format, then click “DOWNLOAD REPORT.” CSV is recommended for
          opening the report directly in Excel. ZIP is available when a compressed download is preferred.
        </Typography>
      </div>
    </section>
  );
};

export default ExportAnalytics;
